import React, { useCallback, useState, useEffect } from 'react';
import { IconButton } from '@atlaskit/button/new';
import CheckCircleIcon from '@atlaskit/icon/core/migration/success--check-circle';
import CopyIcon from '@atlaskit/icon/core/migration/copy';
import { Box, xcss } from '@atlaskit/primitives';
import { G300 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import type { BusinessIssueField } from '@atlassian/jira-business-entity-project/src/services/issue-types-and-fields/types.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { JiraPopup as Popup } from '@atlassian/jira-popup/src/ui/jira-popup.tsx';
import { fireUIAnalytics, type UIAnalyticsEvent } from '@atlassian/jira-product-analytics-bridge';
import {
	SELECTED_CHART_TYPE_QUERY_KEY,
	SELECTED_FIELD_ID_QUERY_KEY,
	SIDEBAR_NAME,
} from '../../../common/constants.tsx';
import messages from './messages.tsx';
import { useClipboardError } from './utils.tsx';

type Props = {
	selectedField: BusinessIssueField | undefined;
	selectedChartType: string | undefined;
};

// to enable mocking with react magnetic di
export const getWindow = () => window;

const PopupContent = () => {
	const { formatMessage } = useIntl();

	return (
		<Box xcss={popupContentContainerStyles}>
			<CheckCircleIcon label="" LEGACY_size="small" color={token('color.icon.success', G300)} />
			&nbsp;
			{formatMessage(messages.linkCopiedPopup)}
		</Box>
	);
};

export const ShareButton = ({ selectedField, selectedChartType }: Props) => {
	const { formatMessage } = useIntl();
	const onClipboardError = useClipboardError();
	const [isCopied, setIsCopied] = useState(false);

	const onClick = useCallback(
		(_: React.MouseEvent, analyticsEvent: UIAnalyticsEvent) => {
			if (selectedField === undefined) return null;

			fireUIAnalytics(analyticsEvent, 'jira.issueFieldStatistics.shareButton', {
				selectedFieldId: selectedField.id,
				selectedChartType,
			});

			const currentWindow: Window | undefined = getWindow();
			const currentNavigator: Navigator | undefined = currentWindow?.navigator;

			if (typeof currentNavigator !== 'undefined') {
				const currentUrl = new URL(currentWindow.location.href);
				currentUrl.searchParams.set('sidebar', SIDEBAR_NAME);
				currentUrl.searchParams.set(SELECTED_FIELD_ID_QUERY_KEY, selectedField.id);

				if (selectedChartType !== undefined) {
					currentUrl.searchParams.set(SELECTED_CHART_TYPE_QUERY_KEY, selectedChartType);
				}

				if (!currentUrl.searchParams.has('filter')) {
					currentUrl.searchParams.set('filter', '');
				}

				// it is valid to have a trailing slash before the query string, but this does not follow our conventions
				const urlString = currentUrl.toString().replace(/\/(?=\?)/, '');

				currentNavigator.clipboard
					.writeText(urlString)
					.then(() => {
						setIsCopied(true);
					})
					.catch((err) => {
						onClipboardError(err);
					});
			} else {
				onClipboardError(new Error('Clipboard is not supported'));
			}
		},
		[selectedField, selectedChartType, onClipboardError],
	);

	// clear popup after 3 seconds
	useEffect(() => {
		let timeout: ReturnType<typeof setTimeout>;
		if (isCopied) {
			timeout = setTimeout(() => {
				setIsCopied(false);
			}, 3000);
		}
		return () => clearTimeout(timeout);
	}, [isCopied]);

	return (
		<Popup
			isOpen={isCopied}
			onClose={() => setIsCopied(false)}
			placement="top-start"
			messageId="business-issue-field-statistics.ui.statistics-sidebar.share-button.popup"
			messageType="transactional"
			content={PopupContent}
			trigger={(triggerProps) => (
				<IconButton
					{...triggerProps}
					appearance="subtle"
					isDisabled={selectedField === undefined}
					icon={CopyIcon}
					label={formatMessage(messages.share)}
					isTooltipDisabled={false}
					onClick={onClick}
				/>
			)}
		/>
	);
};

const popupContentContainerStyles = xcss({
	padding: 'space.200',
});
